/**
 * ImportFromURLPage Components
 * UI components for the URL import page
 */

import React from 'react';
import { Globe, AlertCircle } from 'lucide-react';

interface SupportedSitesProps {
  sites: Array<{ name: string; icon: string }>;
}

export const SupportedSites: React.FC<SupportedSitesProps> = ({ sites }) => (
  <div className="bg-white rounded-lg shadow-sm p-6">
    <div className="flex items-center gap-3 mb-4">
      <Globe className="text-[#4A6651]" size={24} />
      <h2 className="text-lg font-semibold">Supported Sites</h2>
    </div>
    
    <p className="text-gray-600 mb-4">
      We support recipes from most popular cooking websites including:
    </p>
    
    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
      {sites.map((site) => (
        <div
          key={site.name}
          className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg"
        >
          <span className="text-lg">{site.icon}</span>
          <span className="text-sm text-gray-700">{site.name}</span>
        </div>
      ))}
    </div>
    
    <p className="text-sm text-gray-500 mt-4">
      ...and many more! If a site doesn&apos;t work, try copying and pasting the recipe text instead.
    </p>
  </div>
);

export const TipsSection: React.FC = () => (
  <div className="mt-6 p-4 bg-blue-50 rounded-lg">
    <h3 className="font-semibold text-blue-900 mb-2">Tips for successful import:</h3>
    <ul className="text-sm text-blue-800 space-y-1">
      <li>• Make sure to copy the full recipe URL, not just the domain</li>
      <li>• Some sites may require you to be on the actual recipe page, not a preview</li>
      <li>• If import fails, try the &quot;Add Recipe Manually&quot; option with copy/paste</li>
      <li>• Pinterest links should point to the original recipe, not the pin</li>
    </ul>
  </div>
);

interface DebugInfoProps {
  debugInfo: string[];
  onHide: () => void;
}

export const DebugInfo: React.FC<DebugInfoProps> = ({ debugInfo, onHide }) => (
  <div className="bg-gray-100 rounded-lg p-4 mb-6">
    <div className="flex items-center justify-between mb-2">
      <h3 className="text-sm font-semibold text-gray-700">Debug Information</h3>
      <button
        onClick={onHide}
        className="text-xs text-gray-500 hover:text-gray-700"
      >
        Hide
      </button>
    </div>
    <div className="text-xs font-mono text-gray-600 space-y-1">
      {debugInfo.map((info, index) => (
        <div key={index}>{info}</div>
      ))}
    </div>
  </div>
);

interface ErrorMessageProps {
  error: string;
}

export const ErrorMessage: React.FC<ErrorMessageProps> = ({ error }) => (
  <div className="flex items-start gap-2 p-3 bg-red-50 text-red-700 rounded-lg">
    <AlertCircle size={20} className="flex-shrink-0 mt-0.5" />
    <div className="text-sm">{error}</div>
  </div>
);